<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../../stores/authStore';

const { t } = useI18n();

// Lands here after the backend's /auth/callback finishes the OIDC
// exchange and redirects to /login-complete#token=<jwt>&expires_in=...
// The URL fragment is never sent to the server, doesn't appear in the
// Referer header, and isn't logged the way a query string is.

const router = useRouter();
const authStore = useAuthStore();
const error = ref<string | null>(null);

onMounted(async () => {
    try {
        const params = new URLSearchParams(window.location.hash.slice(1));
        const token = params.get('token');
        if (!token) {
            throw new Error('No token in callback URL fragment');
        }

        authStore.setToken(token);

        // Strip the fragment from the URL so it doesn't linger in
        // history or get copied/pasted by the user.
        history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
        );

        // If we deep-linked into a protected route before login, the
        // guard stashed the intended path. Restore it now; otherwise
        // fall back to the default landing page.
        const returnPath = authStore.consumeStashedReturnPath();
        if (returnPath) {
            await router.replace(returnPath);
        } else {
            await router.replace({ name: 'matter-selection' });
        }
    } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
    }
});
</script>

<template>
    <div class="auth-callback">
        <p v-if="!error">{{ t('auth.signingIn') }}</p>
        <p v-else class="auth-callback__error">{{ t('auth.signInFailed', { error }) }}</p>
    </div>
</template>

<style scoped>
.auth-callback {
    padding: 2rem;
    text-align: center;
}
.auth-callback__error {
    color: #b00020;
}
</style>
